/**
 * Welcome component module.
 * @module advisor/pages/Welcome
 *
 */
import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {
  Button,
  Checkbox,
  Column,
  DescriptiveText,
  Icon,
  Image,
  Page,
  Row,
  Tile,
} from '../../shared';
import {_openModal} from '../../shared/reducers/modals';
import history from '../../history';
import axiosInstance from '../../shared/utils/axios';
import {formatter, sanitizeInjection} from '../../shared/utils/utils';

/**
 * Welcome component
 * @name Welcome
 */
const Welcome = ({
  session,
  openModal,
}) => {
  const welcomeMessage = session.profile?.brand?.welcomeMessage;
  const hasCustomMessage = !!welcomeMessage;
  const [hideWelcome, setHideWelcome] = useState(session.profile.hideWelcome);

  const toggleHideWelcome = (isHideWelcomeSelected) => {
    setHideWelcome(isHideWelcomeSelected);
    return axiosInstance.put(`/preferences/users/${session.profile.id}`, {investorLoginHideWelcome: isHideWelcomeSelected})
      .catch(err => {
        setHideWelcome(!isHideWelcomeSelected);
        openModal({
          type: 'Error',
          title: 'Failed to update account preferences',
          err,
        });
      });
  };

  return (
    <Page center={true}>
      <Tile key='Welcome' title='WELCOME' maxWidth={'1000px'}>
        {session.aggregationStatus === 'AUTH_ISSUES' && (
          <Row wrap={false}
               action={() => history.push('/accounts')}
               borderRadius={'6px'}
               margin={'5px 5px 15px 5px'}
               justifySubRow={'left'}
               backgroundColor={'rgba(255,255,255,0.1)'}>
            <Icon svg={'attention'}
                  color={'#FFC003'}
                  height={'20px'}
                  width={'20px'}
                  margin={'5px 15px 5px 15px'}
            />
            <DescriptiveText margin={'5px'} alignment={'left'} layout={{grow: 1}}>
              You have accounts that need your attention. Please click here to update your account(s). If you have any questions,
              please feel free to call Client Services at {formatter('phone')(session.profile?.brand?.investorCSPhone)}.
            </DescriptiveText>
          </Row>
        )}
        <Column margin={'0 20px'}>
          <Row wrap={true} alignItems={'top'} justifySubRow={'center'}>
            <Row width={'100px'} justifySubRow={'left'} margin={'0 0 10px 0'}>
              <Column maxWidth={'100px'}>
                <Image src={session.profile?.firm?.logo}
                       maxHeight={'100px'}
                       maxWidth={'100px'}
                       margin={'0'}/>
              </Column>
            </Row>
            <Row width={'calc(100% - 100px)'} minWidth={'150px'} justifySubRow={'right'}>
              <Column maxWidth={'150px'}>
                <DescriptiveText font={'ProximaRegular'} fontSize={'16px'} alignment={'right'}>
                  Brought to you by:
                </DescriptiveText>
                <Image src={session.profile?.brand?.logo}
                       maxHeight={'150px'}
                       maxWidth={'150px'}
                       margin={'0'}/>
              </Column>
            </Row>
          </Row>
          <Row wrap={false} alignItems={'top'}>
            <DescriptiveText font={'ProximaSemibold'}
                             fontSize={'21px'}
                             margin={'20px 0'}
                             alignment={'center'}>
              Welcome to {session.profile?.brand?.investorPlatformName}
            </DescriptiveText>
          </Row>
          {!hasCustomMessage && <Row wrap={false} alignItems={'top'}>
            <DescriptiveText font={'ProximaRegular'}
                             fontSize={'14px'}
                             margin={'20px 0'}
                             alignment={'left'}>
              {session.profile?.brand?.investorPlatformName} makes it easy for you to gain
              visibility and control over your wealth by providing:
              <ul>
                <li>Your complete financial picture in one place</li>
                <li>The ability to track your progress towards important goals, such as retirement</li>
                <li>Knowledge and insight on your portfolio and how external events may impact your wealth</li>
                <li>Options to compare how you are doing with the market and your peers</li>
              </ul>
            </DescriptiveText>
          </Row>}
          {hasCustomMessage && <Row wrap={false} alignItems={'top'}>
            <DescriptiveText font={'ProximaRegular'}
                             fontSize={'14px'}
                             margin={'20px 0'}
                             alignment={'left'}>
              <span dangerouslySetInnerHTML={{__html: sanitizeInjection(welcomeMessage)}}/>
            </DescriptiveText>
          </Row>}
          <Row wrap={false} alignItems={'top'}>
            <Column>
              <DescriptiveText font={'ProximaSemibold'}
                               fontSize={'18px'}
                               margin={'20px 0 10px 0'}
                               alignment={'center'}>
                Getting started is easy!
              </DescriptiveText>

              <DescriptiveText font={'ProximaRegular'}
                               fontSize={'14px'}
                               margin={'10px 0'}
                               alignment={'left'}>
                Your accounts with {session.profile?.firm?.name} have been added. You can add
                additional accounts now by clicking on “Manually Add Assets” to add your own accounts, or you can
                connect directly to your investment bank, brokerage or credit card accounts by clicking on the
                “Connect to Institution” button.
                <br/><br/>
                If you would rather review the accounts already provided by your advisor please click on the
                “Continue to Overview” button below. Note that you can add more accounts at any time by clicking
                “Add Accounts” at the top of the ACCOUNTS page.

              </DescriptiveText>
            </Column>
          </Row>
          <Row wrap={true} margin={'10px 0 0 0'} alignItems={'center'} justifySubRow={'center'}>
            <Column maxWidth={'165px'}>
              <Button importance={'secondary'}
                      size={'md'}
                      width={'165px'}
                      label={'Manually Add Asset'}
                      action={() => {
                        openModal({
                          type: 'ManuallyAddAssets',
                          onSuccess: () => {
                            history.push('/accounts');
                          },
                        });
                      }}
              />
            </Column>
            <Column maxWidth={'165px'}>
              <Button importance={'secondary'}
                      size={'md'}
                      width={'165px'}
                      label={'Connect to Institution'}
                      action={() => history.push('/accounts/aggregation', {backLocation: '/welcome'})}
                      disabled={!!session.runAsInvestor}/>
            </Column>
            {hasCustomMessage && <Column maxWidth={'165px'}>
              <Button importance={'secondary'}
                      size={'md'}
                      width={'165px'}
                      label={'Continue to Overview'}
                      action={() => axiosInstance.get('/user/institutions')
                        .then(res => res.data.length ? history.push('/portfolio') : openModal({type: 'NoAccountsModal'}))
                        .catch(err => openModal({type: 'Error', err}))}/>
            </Column>}
          </Row>
          {!hasCustomMessage && <Row wrap={false} alignItems={'top'}>
            <DescriptiveText font={'ProximaSemibold'}
                             fontSize={'18px'}
                             margin={'60px 20px 10px 20px'}
                             alignment={'center'}>
              We appreciate your feedback!
            </DescriptiveText>
          </Row>}
          {!hasCustomMessage && <Row wrap={false} alignItems={'top'}>
            <DescriptiveText font={'ProximaRegular'}
                             fontSize={'14px'}
                             margin={'10px 0'}
                             alignment={'left'}>
              Please provide us feedback on what works well for you or where you ran into issues, as well
              as what new features you would like to see in the future. If you get stuck you can use the
              feedback button at the bottom of the screen to let us know where you’re having trouble.
            </DescriptiveText>
          </Row>}
          {!hasCustomMessage && <Row wrap={true} margin={'10px 0 10px 0'} alignItems={'center'} justifySubRow={'center'}>
            <Column maxWidth={'165px'}>
              <Button importance={'secondary'}
                      size={'md'}
                      width={'165px'}
                      label={'Leave Feedback'}
                      action={() => openModal({type: 'FeedbackModal'})}/>
            </Column>
            <Column maxWidth={'165px'}>
              <Button importance={'secondary'}
                      size={'md'}
                      width={'165px'}
                      label={'Continue to Overview'}
                      action={() => axiosInstance.get('/user/institutions')
                        .then(res => res.data.length ? history.push('/portfolio') : openModal({type: 'NoAccountsModal'}))
                        .catch(err => openModal({type: 'Error', err}))}/>
            </Column>
          </Row>}
          <Row wrap={false} margin={'30px 0 0 0'} alignItems={'left'}>
            <Column alignItems={'left'}>
              <Checkbox title={'Hide this message at login.'}
                        margin={'20px 0'}
                        dataPoint={{name: 'hideWelcome', value: hideWelcome}}
                        onChange={(name, value) => toggleHideWelcome(value)}/>
            </Column>
          </Row>
        </Column>
      </Tile>
    </Page>
  );
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = dispatch => ({
  openModal: modal => dispatch(_openModal(modal)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Welcome);

Welcome.propTypes = {
  /**
   * The open modal function to dispatch _openModal from Redux
   */
  openModal: PropTypes.func.isRequired,
  session: PropTypes.shape().isRequired,
};
